import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck, ShieldAlert } from "lucide-react";
import { formatApiError, mailingApi } from "./api";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, st] = await Promise.all([mailingApi.settings.get(), mailingApi.status()]);
        setSettings(s); setDraft(s); setStatus(st);
      } catch (err) {
        toast.error(formatApiError(err, "Failed to load"));
      }
    })();
  }, []);

  if (!draft || !status) return <div className="p-12 text-center velora-micro text-[#3D1F00]/40">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const out = await mailingApi.settings.update(draft);
      setSettings(out); setDraft(out);
      toast.success("Saved");
    } catch (err) { toast.error(formatApiError(err, "Save failed")); }
    finally { setSaving(false); }
  };

  const setFooter = (patch) => setDraft({ ...draft, default_footer: { ...draft.default_footer, ...patch } });
  const setSocial = (patch) => setDraft({ ...draft, social: { ...draft.social, ...patch } });

  return (
    <div className="max-w-3xl space-y-6">
      <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-6">
        <div className="velora-micro text-[#7B3F00] mb-4">System status</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Indicator ok={status.enabled} label="MAILING_ENABLED" />
          <Indicator ok={status.smtp_configured} label="SMTP credentials" />
          <Indicator ok={status.tracking_configured} label="Tracking key" />
        </div>
        <div className="velora-micro text-[#3D1F00]/40 mt-4">
          From:{" "}
          <a
            href={`mailto:${status.from_email}`}
            className="text-[#7B3F00] hover:text-[#3D1F00] transition-colors"
          >
            {status.from_email}
          </a>{" "}
          · Throttle: {status.throttle_per_second}/sec
        </div>
      </div>

      <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-6 space-y-4">
        <div className="velora-micro text-[#7B3F00]">Branding</div>
        <Field label="Logo URL (hosted, must be HTTPS)" value={draft.logo_url || ""} onChange={(x) => setDraft({ ...draft, logo_url: x })} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Primary color (brand brown)" value={draft.brand_primary_color || ""} onChange={(x) => setDraft({ ...draft, brand_primary_color: x })} />
          <Field label="Accent color (amber)" value={draft.brand_accent_color || ""} onChange={(x) => setDraft({ ...draft, brand_accent_color: x })} />
        </div>
      </div>

      <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-6 space-y-4">
        <div className="velora-micro text-[#7B3F00]">Social profiles (icons shown in email footer)</div>
        <Field label="Instagram URL" value={draft.social?.instagram || ""} onChange={(x) => setSocial({ instagram: x })} />
        <Field label="LinkedIn URL" value={draft.social?.linkedin || ""} onChange={(x) => setSocial({ linkedin: x })} />
        <Field label="X / Twitter URL (optional)" value={draft.social?.x || ""} onChange={(x) => setSocial({ x: x })} />
      </div>

      <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-6 space-y-4">
        <div className="velora-micro text-[#7B3F00]">Footer (shown in every email)</div>
        <Field label="Company name" value={draft.default_footer?.company_name || ""} onChange={(x) => setFooter({ company_name: x })} />
        <Field label="Parent / subsidiary line" value={draft.default_footer?.parent_company || ""} onChange={(x) => setFooter({ parent_company: x })} />
        <Field label="Address" value={draft.default_footer?.address || ""} onChange={(x) => setFooter({ address: x })} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone" value={draft.default_footer?.contact_phone || ""} onChange={(x) => setFooter({ contact_phone: x })} />
          <Field label="Website (no https://)" value={draft.default_footer?.website || ""} onChange={(x) => setFooter({ website: x })} />
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="velora-btn-primary flex items-center gap-2">
          <Save size={14}/> {saving ? "Saving..." : "Save settings"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <label className="velora-micro text-[#3D1F00]/50 block mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="velora-input w-full" />
    </div>
  );
}

function Indicator({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 p-3 border ${ok ? "border-[#E0D4B6] text-[#4A6741]" : "border-[#C0392B] text-[#C0392B]"}`}>
      {ok ? <ShieldCheck size={14}/> : <ShieldAlert size={14}/>}
      <span className="text-sm">{label}</span>
    </div>
  );
}
